import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';

const sources = ['', 'backend', 'frontend', 'user_report'];
const levels = ['', 'error', 'warning', 'info'];
const sinceOpts = [
  { v: '', label: 'wszystko' },
  { v: 1, label: '1h' },
  { v: 24, label: '24h' },
  { v: 168, label: '7d' },
];

export default function ErrorsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const [filterSource, setFilterSource] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterResolved, setFilterResolved] = useState('false'); // domyślnie tylko nierozwiązane
  const [filterSearch, setFilterSearch] = useState('');
  const [filterSince, setFilterSince] = useState('');
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (filterSource) params.source = filterSource;
      if (filterLevel) params.level = filterLevel;
      if (filterResolved !== '') params.resolved = filterResolved;
      if (filterSearch) params.search = filterSearch;
      if (filterSince) params.since_hours = filterSince;
      const res = await api.get('/errors', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [skip, filterSource, filterLevel, filterResolved, filterSearch, filterSince]);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/errors/stats');
      setStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const resolveOne = async (id) => {
    await api.post(`/errors/${id}/resolve`);
    load(); loadStats();
  };
  const deleteOne = async (id) => {
    if (!window.confirm('Usunąć ten log?')) return;
    await api.delete(`/errors/${id}`);
    load(); loadStats();
  };
  const clearResolved = async () => {
    if (!window.confirm('Usunąć WSZYSTKIE rozwiązane logi?')) return;
    await api.post('/errors/clear-resolved');
    load(); loadStats();
  };

  return (
    <div style={{ padding: 24, color: '#e6e6e6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600 }}>Monitor błędów</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { load(); loadStats(); }} style={btn}>Odśwież</button>
          <button onClick={clearResolved} style={{ ...btn, background: '#3a1a1a' }}>Wyczyść rozwiązane</button>
        </div>
      </div>

      {stats && (
        <div style={statsRow}>
          <div style={statBox}><div style={statN}>{stats.total}</div><div style={statL}>łącznie</div></div>
          <div style={{ ...statBox, background: '#3a1a1a' }}><div style={statN}>{stats.unresolved}</div><div style={statL}>nierozwiązane</div></div>
          {stats.buckets?.filter((b) => !b._id.resolved).slice(0, 4).map((b, i) => (
            <div key={i} style={statBox}>
              <div style={statN}>{b.count}</div>
              <div style={statL}>{b._id.source} / {b._id.level}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={filterSource} onChange={(e) => { setSkip(0); setFilterSource(e.target.value); }} style={input}>
          {sources.map((s) => <option key={s} value={s}>{s || 'źródło: dowolne'}</option>)}
        </select>
        <select value={filterLevel} onChange={(e) => { setSkip(0); setFilterLevel(e.target.value); }} style={input}>
          {levels.map((l) => <option key={l} value={l}>{l || 'poziom: dowolny'}</option>)}
        </select>
        <select value={filterResolved} onChange={(e) => { setSkip(0); setFilterResolved(e.target.value); }} style={input}>
          <option value="false">tylko nierozwiązane</option>
          <option value="true">tylko rozwiązane</option>
          <option value="">wszystkie</option>
        </select>
        <select value={filterSince} onChange={(e) => { setSkip(0); setFilterSince(e.target.value); }} style={input}>
          {sinceOpts.map((o) => <option key={o.v} value={o.v}>okres: {o.label}</option>)}
        </select>
        <input
          value={filterSearch}
          onChange={(e) => { setSkip(0); setFilterSearch(e.target.value); }}
          placeholder="szukaj (message / path / stack / email)"
          style={{ ...input, flex: 1, minWidth: 240 }}
        />
      </div>

      <div style={{ background: '#1a1a22', borderRadius: 6, overflow: 'hidden', border: '1px solid #2a2a35' }}>
        <div style={hdrRow}>
          <div style={{ flex: '0 0 160px' }}>czas</div>
          <div style={{ flex: '0 0 90px' }}>źródło</div>
          <div style={{ flex: '0 0 70px' }}>poziom</div>
          <div style={{ flex: 1 }}>wiadomość / ścieżka</div>
          <div style={{ flex: '0 0 100px' }}>akcje</div>
        </div>
        {loading && <div style={{ padding: 16, opacity: 0.7 }}>Ładuję…</div>}
        {!loading && items.length === 0 && <div style={{ padding: 16, opacity: 0.7 }}>Brak błędów dla aktualnych filtrów.</div>}
        {items.map((e) => (
          <div key={e.id}>
            <div
              style={{ ...row, background: e.resolved ? '#16201a' : (e.level === 'error' ? '#1f1416' : 'transparent') }}
              onClick={() => toggleExpand(e.id)}
            >
              <div style={{ flex: '0 0 160px', fontSize: 12, opacity: 0.8 }}>
                {(e.last_seen || e.ts)?.replace('T', ' ').slice(0, 19)}
                {e.occurrence_count > 1 && (
                  <span style={{ marginLeft: 6, padding: '1px 6px', background: '#5f1e1e', color: '#fff', borderRadius: 3, fontSize: 10, fontWeight: 600 }}>
                    ×{e.occurrence_count}
                  </span>
                )}
              </div>
              <div style={{ flex: '0 0 90px' }}><span style={badge(e.source)}>{e.source}</span></div>
              <div style={{ flex: '0 0 70px' }}><span style={badgeLvl(e.level)}>{e.level}</span></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.last_message || e.message}</div>
                {e.path && <div style={{ fontSize: 11, opacity: 0.6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.method && `${e.method} `}{e.path}{e.status_code && ` → ${e.status_code}`}</div>}
              </div>
              <div style={{ flex: '0 0 100px', display: 'flex', gap: 4 }} onClick={(ev) => ev.stopPropagation()}>
                {!e.resolved && <button onClick={() => resolveOne(e.id)} style={btnMini}>✓</button>}
                <button onClick={() => deleteOne(e.id)} style={{ ...btnMini, color: '#e88' }}>×</button>
              </div>
            </div>
            {expanded.has(e.id) && (
              <div style={detailBox}>
                <div style={detailRow}><b>id:</b> {e.id}</div>
                {e.fingerprint && <div style={detailRow}><b>fingerprint:</b> {e.fingerprint.slice(0, 16)}…</div>}
                {e.occurrence_count > 1 && (
                  <div style={detailRow}>
                    <b>wystąpień:</b> {e.occurrence_count} (pierwsze: {e.first_seen?.replace('T', ' ').slice(0, 19)}, ostatnie: {e.last_seen?.replace('T', ' ').slice(0, 19)})
                  </div>
                )}
                {e.user_email && <div style={detailRow}><b>user:</b> {e.user_email}</div>}
                {e.user_agent && <div style={detailRow}><b>UA:</b> {e.user_agent}</div>}
                {e.ip && <div style={detailRow}><b>IP:</b> {e.ip}</div>}
                {e.stack && <div style={detailRow}><b>stack:</b><pre style={pre}>{e.stack}</pre></div>}
                {e.context && Object.keys(e.context).length > 0 && (
                  <div style={detailRow}><b>context:</b><pre style={pre}>{JSON.stringify(e.context, null, 2)}</pre></div>
                )}
                {e.resolved && <div style={{ ...detailRow, color: '#7cd97c' }}>Rozwiązane przez {e.resolved_by} o {e.resolved_at}</div>}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center', justifyContent: 'flex-end' }}>
        <span style={{ opacity: 0.7, fontSize: 13 }}>{skip + 1}-{Math.min(skip + items.length, total)} z {total}</span>
        <button onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0} style={btn}>‹ poprzednia</button>
        <button onClick={() => setSkip(skip + limit)} disabled={skip + limit >= total} style={btn}>następna ›</button>
      </div>
    </div>
  );
}

const btn = { padding: '6px 12px', background: '#2a2a35', color: '#e6e6e6', border: '1px solid #3a3a45', borderRadius: 6, cursor: 'pointer', fontSize: 13 };
const btnMini = { padding: '2px 8px', background: 'transparent', color: '#e6e6e6', border: '1px solid #3a3a45', borderRadius: 4, cursor: 'pointer', fontSize: 13 };
const input = { padding: '6px 10px', background: '#1a1a22', color: '#e6e6e6', border: '1px solid #2a2a35', borderRadius: 6, fontSize: 13 };
const hdrRow = { display: 'flex', padding: '10px 12px', borderBottom: '1px solid #2a2a35', background: '#15151c', fontSize: 12, opacity: 0.7, fontWeight: 600 };
const row = { display: 'flex', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #2a2a35', cursor: 'pointer', fontSize: 13, gap: 8 };
const detailBox = { padding: 12, background: '#0f0f14', borderBottom: '1px solid #2a2a35', fontSize: 12 };
const detailRow = { marginBottom: 8 };
const pre = { background: '#1a1a22', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 300, fontSize: 11, marginTop: 4 };
const statsRow = { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' };
const statBox = { background: '#1a1a22', padding: '8px 14px', borderRadius: 6, border: '1px solid #2a2a35', minWidth: 80 };
const statN = { fontSize: 18, fontWeight: 600 };
const statL = { fontSize: 11, opacity: 0.7 };
const badge = (s) => ({
  fontSize: 10, padding: '2px 6px', borderRadius: 3,
  background: s === 'backend' ? '#1e3a5f' : s === 'frontend' ? '#3a1e5f' : '#1e5f3a',
});
const badgeLvl = (l) => ({
  fontSize: 10, padding: '2px 6px', borderRadius: 3,
  background: l === 'error' ? '#5f1e1e' : l === 'warning' ? '#5f4a1e' : '#1e3a5f',
});
